import { z } from "zod";
import { ENGAGEMENT_SHAPES, SIZE_TIERS } from "../intake/classification";
import { FINDING_SEVERITIES, MODEL_FINDING_TYPES } from "../intake/findings";

/**
 * What the model returns on every conversation turn (agent-design §4, ADR-0006). One
 * streamed object; `reply` comes first so it can be shown while the rest is generated.
 * Every property is required (absent values are `null` or `[]`): providers' strict
 * structured-output modes reject optional properties.
 * The model proposes; the validators (apps/api/src/modules/conversation) decide.
 */
export const turnUpdateSchema = z.object({
  fieldKey: z.string().describe("A field key from the field rules."),
  value: z
    .union([z.string(), z.array(z.string()), z.null()])
    .describe("The value in the requester's own words (list fields: array). null when status is not CAPTURED."),
  status: z.enum(["CAPTURED", "NOT_APPLICABLE", "NOT_PROVIDED"]),
  source: z.enum(["REQUESTER", "DOCUMENT"]),
  evidence: z.string().describe("Verbatim quote from the requester's message or the document that supports the value."),
  documentId: z.string().nullable(),
  isRevision: z.boolean().describe("true only when the requester explicitly changes a value captured earlier."),
});
export type TurnUpdate = z.infer<typeof turnUpdateSchema>;

export const turnFindingSchema = z.object({
  type: z.enum(MODEL_FINDING_TYPES),
  severity: z.enum(FINDING_SEVERITIES),
  message: z.string().describe("One sentence for the buyer."),
  evidence: z.string().nullable(),
});
export type TurnFinding = z.infer<typeof turnFindingSchema>;

export const turnResolvedFindingSchema = z.object({
  ref: z.string().describe("The reference of an open finding in the request record (e.g. F2)."),
  resolution: z.string().describe("One sentence for the buyer: how the requester's answer settles it."),
});
export type TurnResolvedFinding = z.infer<typeof turnResolvedFindingSchema>;

export const turnOutputSchema = z.object({
  reply: z.string().describe("What the requester sees, in the conversation language."),
  language: z.enum(["it", "en"]),
  updates: z.array(turnUpdateSchema),
  findings: z.array(turnFindingSchema),
  resolvedFindings: z
    .array(turnResolvedFindingSchema)
    .describe("Open findings the requester's answers have now settled (empty when none)."),
  engagementShape: z.enum(ENGAGEMENT_SHAPES).nullable(),
  sizeTier: z.enum(SIZE_TIERS).nullable(),
  askedFields: z.array(z.string()).describe("Field keys the question in `reply` asks about (empty if no question)."),
  suggestSubmit: z.boolean(),
});
export type TurnOutput = z.infer<typeof turnOutputSchema>;
